import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMaturityFrameworks } from '../../../graphql/queries';
import {
  DataTable,
  EmptyState,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../../components/shared';
import type { MaturityFramework } from '../../../graphql/types';
import '@tylertech/forge/button';
import './index.css';

export const Route = createFileRoute('/maturity/frameworks/')({
  component: MaturityFrameworksListPage,
});

export { MaturityFrameworksListPage };

const columns: DataTableColumn<MaturityFramework>[] = [
  { key: 'name', label: 'Name', sortable: true, accessor: (fw) => fw.name },
  { key: 'version', label: 'Version', sortable: true, accessor: (fw) => fw.version },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    render: (fw) => (
      <span className={`fw-status-badge fw-status-badge--${fw.status.toLowerCase()}`}>
        {fw.status}
      </span>
    ),
  },
  {
    key: 'areas',
    label: 'Categories',
    render: (fw) => {
      const count = fw.areas.reduce((acc, a) => acc + a.categories.length, 0);
      return <span>{count}</span>;
    },
  },
  {
    key: 'effectiveFrom',
    label: 'Effective From',
    render: (fw) => new Date(fw.effectiveFrom).toLocaleDateString(),
  },
];

function MaturityFrameworksListPage(): JSX.Element {
  const ForgeButton = 'forge-button' as React.ElementType;
  const navigate = useNavigate();
  const { data, loading, error } = useMaturityFrameworks();

  const frameworks = data?.maturityFrameworks?.edges?.map((e) => e.node) ?? [];

  const handleRowClick = (fw: MaturityFramework): void => {
    void navigate({
      to: '/maturity/frameworks/$frameworkId',
      params: { frameworkId: fw.entityId },
    });
  };

  const handleCreate = (): void => {
    void navigate({ to: '/maturity/frameworks/new' });
  };

  if (loading) {
    return (
      <div className="frameworks-list-page">
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="frameworks-list-page">
        <EmptyState icon="error" title="Failed to load frameworks" description={error.message} />
      </div>
    );
  }

  return (
    <div className="frameworks-list-page">
      <div className="page-header page-header--toolbar">
        <div className="page-header-title page-header-title--column">
          <h1 className="forge-typography--headline5">Maturity Frameworks</h1>
          <p className="forge-typography--body2">
            {frameworks.length} framework{frameworks.length !== 1 ? 's' : ''}
          </p>
        </div>

        <PermissionGate permission="maturityFramework:create">
          <ForgeButton type="button" variant="raised" onClick={handleCreate}>
            Create Framework
          </ForgeButton>
        </PermissionGate>
      </div>

      {frameworks.length === 0 ? (
        <EmptyState
          icon="assignment"
          title="No maturity frameworks"
          description="Create a framework to start assessing service maturity."
        />
      ) : (
        <DataTable
          columns={columns}
          data={frameworks}
          getRowKey={(fw) => fw.entityId}
          onRowClick={handleRowClick}
        />
      )}
    </div>
  );
}
